'use client';

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
  TooltipItem,
  ChartEvent,
  ActiveElement
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useState } from 'react';
import styles from '../ShareholdingCharts.module.css';
import { LABELMAP } from '../label-constants';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Define the data point structure
interface ShareholdingDataPoint {
  date: Date;
  [key: string]: number | Date | string; // Dynamic fields for shareholding percentages
}

// Define component props
interface LineChartProps {
  data: ShareholdingDataPoint[];
  series: string[];
  title?: string;
  description?: string;
}

export function LineChart({ data, series, title, description }: LineChartProps): React.ReactElement {
  // Track which series should be filled - default to no fill
  const [filledSeries, setFilledSeries] = useState<number>(-1);

  // Prepare data
  const labels: string[] = data.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short' });
  });

  // Chart colors
  const colors: string[] = [
    'rgb(59, 130, 246)', // Blue
    'rgb(16, 185, 129)', // Green
    'rgb(249, 115, 22)', // Orange
    'rgb(139, 92, 246)', // Purple
    'rgb(239, 68, 68)',  // Red
  ];

  // Get label from LABELMAP or format the key
  const getLabel = (key: string): string => {
    return LABELMAP[key] || key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const chartData: ChartData<'line'> = {
    labels,
    datasets: series.map((key, index) => ({
      label: getLabel(key),
      data: data.map(d => Number(d[key]) || 0),
      borderColor: colors[index % colors.length],
      backgroundColor: `${colors[index % colors.length]}10`,
      pointRadius: 0,
      pointHoverRadius: 5,
      // Add these properties to fix the hover point color
      pointHoverBackgroundColor: colors[index % colors.length],
      pointHoverBorderColor: colors[index % colors.length],
      borderWidth: 2,
      tension: 0.3,
      fill: index === filledSeries,
      order: series.length - index,
    })),
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          color: 'rgb(156, 163, 175)',
          usePointStyle: true,
          boxWidth: 6,
          boxHeight: 6,
          padding: 25,
          font: {
            size: 12,
          },
          pointStyle: 'line',
          // // Fix the legend color to match the line color
          // generateLabels: (chart) => {
          //   const datasets = chart.data.datasets;
          //   return datasets.map((dataset, i) => {
          //     return {
          //       text: dataset.label || '',
          //       fillStyle: dataset.borderColor as string,
          //       strokeStyle: dataset.borderColor as string,
          //       lineWidth: 2,
          //       hidden: !chart.isDatasetVisible(i),
          //       index: i,
          //       datasetIndex: i
          //     };
          //   });
          // }
        },
        onClick: (e: ChartEvent, legendItem: any, legend: any) => {
          // Get the index of the clicked dataset
          const index = legendItem.datasetIndex;

          // Toggle the dataset visibility (default Chart.js behavior)
          const chart = legend.chart;
          const datasetMeta = chart.getDatasetMeta(index);
          datasetMeta.hidden = datasetMeta.hidden === null ?
            !chart.data.datasets[index].hidden : null;

          // Count visible datasets
          const visibleDatasets = chart.data.datasets
            .filter((_: any, i: number) => !chart.getDatasetMeta(i).hidden).length;

          // If only one dataset is visible, fill it
          if (visibleDatasets === 1 && !datasetMeta.hidden) {
            setFilledSeries(index);
          } else {
            setFilledSeries(-1);
          }

          // Update the chart
          chart.update();
        }
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'line'>) =>
            `${context.dataset.label}: ${Number(context.raw).toFixed(2)}%`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        border: {
          display: false,
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          callback: (tickValue: string | number) => `${tickValue}%`,
          maxTicksLimit: 5,
          font: {
            size: 11,
          }
        }
      },
      x: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          maxTicksLimit: 6,
          maxRotation: 0,
          font: {
            size: 11,
          }
        }
      }
    }
  };

  return (
    <div className={styles.chartWrapper}>
      <Line data={chartData} options={options} />
    </div>
  );
}